import type { ReactNode } from "react";
import { cx } from "shared/utils";

interface AvatarProps {
  name?: string;
  size?: number;
  shape?: "circle" | "square";
  src?: string;
  // Rendered when there is no src and no name. `name` must be a person's
  // name — initials are derived from it, so a phone number or other
  // non-name string produces garbage like "+(".
  fallbackText?: ReactNode;
  fallbackBackgroundColor?: string;
  fallbackTextColor?: string;
  className?: string;
}

const getBorderRadius = (shape: "circle" | "square") => {
  switch (shape) {
    case "circle":
      return "rounded-full";
    case "square":
      return "rounded";
    default:
      return "rounded-full";
  }
};

const getInitials = (name?: string) => {
  if (!name) return "?";

  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const stringToColor = (str?: string) => {
  if (!str) return "#e6007e";

  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + hash * 31;

  const hue = hash % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

export const Avatar = (props: AvatarProps) => {
  const {
    name,
    size = 12,
    shape = "circle",
    src,
    className = "",
    fallbackText,
    fallbackBackgroundColor,
    fallbackTextColor
  } = props;

  return (
    <div
      className={cx(
        "grid place-items-center overflow-hidden shrink-0",
        getBorderRadius(shape)
      )}
      style={{
        height: `${size}px`,
        width: `${size}px`
      }}
      tabIndex={-1}
    >
      {src ? (
        <img
          src={src}
          className={cx("h-full w-full", getBorderRadius(shape), className)}
          alt={name}
        />
      ) : (
        <div
          className={cx(
            "grid h-full w-full place-items-center text-center leading-none",
            getBorderRadius(shape),
            className
          )}
          style={{
            backgroundColor: fallbackBackgroundColor ?? stringToColor(name),
            color: fallbackTextColor ?? "#ffffff"
          }}
        >
          {name ? getInitials(name) : (fallbackText ?? "?")}
        </div>
      )}
    </div>
  );
};
